import { useState, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Upload, FileSpreadsheet, Loader2, X } from 'lucide-react';
import { SpreadsheetPreview } from './SpreadsheetPreview';
import { parseFile, getSheetData } from '../utils/parseSpreadsheet';
import { cn } from '@/lib/utils';

export function UploadStep({ onFileLoaded, fileData, setFileData }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const handleFile = useCallback(async (file) => {
    if (!file) return;

    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/csv'
    ];
    const validExtensions = ['.xlsx', '.xls', '.csv'];
    const ext = '.' + file.name.split('.').pop().toLowerCase();

    if (!validTypes.includes(file.type) && !validExtensions.includes(ext)) {
      setError('Please upload an Excel (.xlsx, .xls) or CSV (.csv) file');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await parseFile(file);
      const initialSheet = result.sheetNames[0];
      const sheetData = result.sheets[initialSheet];

      setFileData({
        fileName: file.name,
        workbook: result.workbook,
        sheetNames: result.sheetNames,
        activeSheet: initialSheet,
        sheetData
      });

      onFileLoaded?.(result);
    } catch (err) {
      console.error('Error parsing file:', err);
      setError('Failed to parse file. Please check it is a valid spreadsheet.');
    } finally {
      setLoading(false);
    }
  }, [onFileLoaded, setFileData]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    handleFile(file);
  }, [handleFile]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const handleInputChange = useCallback((e) => {
    const file = e.target.files[0];
    handleFile(file);
    e.target.value = '';
  }, [handleFile]);

  const handleSheetChange = useCallback(async (sheetName) => {
    if (!fileData?.workbook) return;
    const sheetData = await getSheetData(fileData.workbook, sheetName);
    setFileData(prev => ({ ...prev, activeSheet: sheetName, sheetData }));
  }, [fileData?.workbook, setFileData]);

  const handleClear = useCallback(() => {
    setFileData(null);
    setError(null);
  }, [setFileData]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <p className="mt-3 text-sm text-muted-foreground">Parsing spreadsheet...</p>
      </div>
    );
  }

  if (fileData) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-green-600" />
            <span className="font-medium text-sm">{fileData.fileName}</span>
            <span className="text-xs text-muted-foreground">
              ({fileData.sheetData?.length || 0} rows)
            </span>
          </div>
          <Button variant="ghost" size="sm" onClick={handleClear}>
            <X className="h-4 w-4 mr-1" />
            Remove
          </Button>
        </div>

        {fileData.sheetNames.length > 1 && (
          <div className="flex items-center gap-1 overflow-x-auto pb-1">
            {fileData.sheetNames.map((name) => (
              <button
                key={name}
                onClick={() => handleSheetChange(name)}
                className={cn(
                  'px-3 py-1 text-xs rounded border transition-colors whitespace-nowrap',
                  fileData.activeSheet === name
                    ? 'bg-white border-gray-300 font-medium shadow-sm'
                    : 'bg-gray-50 border-transparent hover:bg-gray-100'
                )}
              >
                {name}
              </button>
            ))}
          </div>
        )}

        <SpreadsheetPreview data={fileData.sheetData} maxRows={15} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => fileInputRef.current?.click()}
        className={cn(
          'border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-all',
          dragOver
            ? 'border-blue-400 bg-blue-50'
            : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
        )}
      >
        <Upload className={cn('h-10 w-10 mx-auto mb-4', dragOver ? 'text-blue-500' : 'text-gray-400')} />
        <p className="text-base font-medium text-gray-700">
          Drop your spreadsheet here or click to browse
        </p>
        <p className="text-sm text-muted-foreground mt-1">
          Supports .xlsx, .xls, and .csv files
        </p>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls,.csv"
        onChange={handleInputChange}
        className="hidden"
      />

      {error && (
        <div className="text-sm text-red-600 bg-red-50 rounded-lg p-3 border border-red-200">
          {error}
        </div>
      )}
    </div>
  );
}
